import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterOutlet } from '@angular/router';

import { AuthService } from '../../core/services/auth.service';
import { SidebarService } from '../../core/services/sidebar.service';
import { EmaShellComponent, EmaMenuSection } from '../../shared/components/ema-shell/ema-shell.component';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, ReactiveFormsModule, EmaShellComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-ema-shell
      title="EMA - Quản lý sức khỏe dân số"
      [badges]="['Trạm Y Tế Bình Lợi Trung']"
      [menuNhom]="menuNhom()"
      [sections]="sections">
      <router-outlet></router-outlet>
    </app-ema-shell>

    @if (showChangePassword()) {
      <div class="pwd-backdrop" (click)="closeChangePassword()">
        <div class="pwd-dialog" (click)="$event.stopPropagation()">
          <div class="pwd-head">
            <div class="pwd-title">Đổi mật khẩu</div>
            @if (!isForced() && !passwordSuccess()) {
              <button type="button" class="pwd-close" (click)="closeChangePassword()">×</button>
            }
          </div>

          @if (passwordSuccess()) {
            <div class="pwd-form">
              <div class="pwd-success">{{ passwordSuccess() }}</div>
              <div class="pwd-actions">
                <button type="button" class="pwd-btn primary" (click)="confirmPasswordSuccess()">Xác nhận</button>
              </div>
            </div>
          } @else {
            <form class="pwd-form" [formGroup]="changePasswordForm" (ngSubmit)="submitChangePassword()">
              @if (isForced()) {
                <div class="pwd-notice">Bạn cần đổi mật khẩu trước khi sử dụng hệ thống.</div>
              }

              <label class="pwd-label" for="currentPassword">Mật khẩu hiện tại</label>
              <div class="pwd-input-wrap">
                <input id="currentPassword" class="pwd-input" [type]="showCurrentPassword() ? 'text' : 'password'" formControlName="currentPassword" />
                <button type="button" class="pwd-toggle" (click)="showCurrentPassword.set(!showCurrentPassword())">
                  {{ showCurrentPassword() ? 'Ẩn' : 'Hiện' }}
                </button>
              </div>

              <label class="pwd-label" for="newPassword">Mật khẩu mới</label>
              <div class="pwd-input-wrap">
                <input id="newPassword" class="pwd-input" [type]="showNewPassword() ? 'text' : 'password'" formControlName="newPassword" />
                <button type="button" class="pwd-toggle" (click)="showNewPassword.set(!showNewPassword())">
                  {{ showNewPassword() ? 'Ẩn' : 'Hiện' }}
                </button>
              </div>

              <label class="pwd-label" for="confirmPassword">Nhập lại mật khẩu mới</label>
              <div class="pwd-input-wrap">
                <input id="confirmPassword" class="pwd-input" [type]="showConfirmPassword() ? 'text' : 'password'" formControlName="confirmPassword" />
                <button type="button" class="pwd-toggle" (click)="showConfirmPassword.set(!showConfirmPassword())">
                  {{ showConfirmPassword() ? 'Ẩn' : 'Hiện' }}
                </button>
              </div>

              @if (passwordError()) {
                <div class="pwd-error">{{ passwordError() }}</div>
              }

              <div class="pwd-actions">
                @if (!isForced()) {
                  <button type="button" class="pwd-btn ghost" (click)="closeChangePassword()">Hủy</button>
                }
                <button type="submit" class="pwd-btn primary">Đổi mật khẩu</button>
              </div>
            </form>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    .pwd-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.42);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      z-index: 1200;
    }

    .pwd-dialog {
      width: min(100%, 420px);
      background: #ffffff;
      border-radius: 16px;
      box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
      border: 1px solid rgba(148, 163, 184, 0.2);
      overflow: hidden;
    }

    .pwd-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 18px 20px 14px;
      border-bottom: 1px solid #e5e7eb;
      background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
    }

    .pwd-title {
      font-size: 16px;
      font-weight: 700;
      color: #0f172a;
    }

    .pwd-close {
      width: 30px;
      height: 30px;
      border: 0;
      border-radius: 8px;
      background: transparent;
      color: #64748b;
      font-size: 20px;
      cursor: pointer;
    }

    .pwd-close:hover {
      background: #f1f5f9;
      color: #0f172a;
    }

    .pwd-form {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 18px 20px 20px;
    }

    .pwd-label {
      font-size: 12px;
      font-weight: 600;
      color: #475569;
    }

    .pwd-input {
      width: 100%;
      height: 40px;
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      padding: 0 56px 0 12px;
      font-size: 13px;
      color: #0f172a;
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s;
    }

    .pwd-input-wrap {
      position: relative;
    }

    .pwd-toggle {
      position: absolute;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      border: 0;
      background: transparent;
      color: #1b4f8a;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;
      padding: 0;
    }

    .pwd-input:focus {
      border-color: #1b4f8a;
      box-shadow: 0 0 0 3px rgba(27, 79, 138, 0.12);
    }

    .pwd-error {
      font-size: 12px;
      color: #dc2626;
      background: #fff1f2;
      border: 1px solid #fecdd3;
      border-radius: 10px;
      padding: 9px 10px;
      margin-top: 2px;
    }

    .pwd-success,
    .pwd-notice {
      font-size: 12px;
      border-radius: 10px;
      padding: 10px 12px;
    }

    .pwd-success {
      color: #166534;
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
    }

    .pwd-notice {
      color: #92400e;
      background: #fffbeb;
      border: 1px solid #fcd34d;
    }

    .pwd-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 8px;
    }

    .pwd-btn {
      height: 38px;
      padding: 0 16px;
      border-radius: 10px;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #475569;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
    }

    .pwd-btn.primary {
      border-color: #1b4f8a;
      background: #1b4f8a;
      color: #ffffff;
    }

    .pwd-btn.ghost:hover,
    .pwd-btn.primary:hover {
      opacity: 0.9;
    }
  `],
})
export class MainLayoutComponent {
  private readonly authService = inject(AuthService);
  private readonly sidebarService = inject(SidebarService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  readonly showChangePassword = signal(false);
  readonly passwordError = signal<string | null>(null);
  readonly passwordSuccess = signal<string | null>(null);
  readonly showCurrentPassword = signal(false);
  readonly showNewPassword = signal(false);
  readonly showConfirmPassword = signal(false);
  readonly isForced = computed(() => this.authService.currentUser()?.is_changepassword === true);
  readonly menuNhom = this.sidebarService.nhom;

  readonly changePasswordForm = this.fb.nonNullable.group({
    currentPassword: ['', Validators.required],
    newPassword: ['', Validators.required],
    confirmPassword: ['', Validators.required],
  });

  readonly sections: EmaMenuSection[] = [
    {
      title: 'Hệ thống',
      items: [
        { label: 'Đăng xuất', icon: '', action: () => this.logout() },
        { label: 'Đổi mật khẩu', icon: '', action: () => this.openChangePassword() },
      ],
    },
  ];

  constructor() {
    if (this.authService.currentUser()?.is_changepassword) {
      this.showChangePassword.set(true);
    }
    const nhanvien_id = this.authService.currentUser()?.nhanvien_id;
    if (nhanvien_id) {
      this.sidebarService.load(nhanvien_id);
    }
  }

  openChangePassword(): void {
    this.changePasswordForm.reset({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    this.passwordError.set(null);
    this.passwordSuccess.set(null);
    this.showCurrentPassword.set(false);
    this.showNewPassword.set(false);
    this.showConfirmPassword.set(false);
    this.showChangePassword.set(true);
  }

  closeChangePassword(): void {
    if (this.isForced()) return;
    this.showChangePassword.set(false);
    this.passwordError.set(null);
    this.passwordSuccess.set(null);
  }

  submitChangePassword(): void {
    const form = this.changePasswordForm.getRawValue();

    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      this.passwordError.set('Vui lòng nhập đủ 3 trường.');
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      this.passwordError.set('Mật khẩu mới và xác nhận mật khẩu chưa khớp.');
      return;
    }

    if (form.newPassword.length < 8) {
      this.passwordError.set('Mật khẩu mới phải có ít nhất 8 ký tự.');
      return;
    }

    this.passwordError.set(null);
    this.authService.changePassword(form.currentPassword, form.newPassword).subscribe({
      next: () => {
        if (this.isForced()) {
          this.passwordSuccess.set('Mật khẩu đã được cập nhật. Vui lòng đăng nhập lại.');
        } else {
          this.authService.patchIsChangePassword(false);
          this.passwordSuccess.set('Đổi mật khẩu thành công.');
        }
      },
      error: (err) => {
        this.passwordError.set(err?.error?.error || 'Không thể đổi mật khẩu.');
      },
    });
  }

  confirmPasswordSuccess(): void {
    if (this.isForced()) {
      this.authService.logout();
      this.router.navigate(['/login']);
      return;
    }

    this.showChangePassword.set(false);
    this.passwordSuccess.set(null);
  }

  logout(): void {
    this.sidebarService.clear();
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
